import { useEffect, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { apiService } from '../services/api';

const num = (v?: number | null, d = 2) => (v == null ? '–' : v.toFixed(d));
const signed = (v?: number | null, d = 2) => (v == null ? '–' : `${v > 0 ? '+' : ''}${v.toFixed(d)}`);
const color = (v?: number | null) => (v == null ? undefined : v > 0 ? 'success.main' : v < 0 ? 'error.main' : undefined);
const FAMILY: Record<string, string> = {
  price: 'Prices', earnings: 'Earnings', shorts: 'Short sellers', options: 'Options', funda: 'Accounts',
};
const DESIGN_ORDER = [
  'SPY', '70% SPY + 30% calmer momentum', 'momentum top 30 without the most volatile fifth', '70% SPY + 30% momentum',
  '50% SPY + 50% momentum', 'momentum top 30', 'momentum top 15', 'momentum top 30, quarterly',
  '70/30, sleeve at 15% volatility', '70/30, T-bills below the 10-month trend',
];
const INDEX_ROWS = [
  'buy & hold 1×', 'growth-trend 1×', 'trend 1×', 'daily 200-day 1×', 'buy & hold 1.5×', 'growth-trend 1.5×',
  'growth-trend 1.5×, bonds when out', 'growth-trend 2×', 'daily 200-day 2×', 'vol target 15% ≤2× while growth-trend in',
  'dip reserve 80% → 100% → 120%', '1.5× in a 20% fall', '1.5× in a 20% fall, growth-trend in',
];

function Verdict({ cell, explore }: { cell: any; explore?: boolean }) {
  if (!cell) return <Chip size="small" variant="outlined" label="no data" />;
  if (explore) return <Chip size="small" variant="outlined" label="exploratory" />;
  if (cell.passes) return <Chip size="small" color="success" label="works" />;
  if (cell.q != null && cell.q < 0.05 && cell.net_pct_month < 0) return <Chip size="small" color="error" label="loses" />;
  const h = Object.values(cell.halves || {}) as (number | null)[];
  if (cell.net_pct_month > 0 && h.every(x => (x ?? 0) > 0)) return <Chip size="small" color="info" label="positive, not proven" />;
  return <Chip size="small" label="no edge" />;
}

export default function SignalLab() {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [univ, setUniv] = useState<'large' | 'small' | 'all'>('large');
  useEffect(() => { apiService.getSignalLab().then(setData).catch(e => setError(e?.message || 'Failed to load')); }, []);

  const lab = data?.lab;
  const pub = data?.published;
  const pa = pub?.all;
  const pi = pub?.investable;
  const designs = lab?.designs || {};
  const book = data?.book;
  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Signal Lab</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 950 }}>
        Before inventing more signals: which of the published ones still work? Twenty-four stock signals with a
        published, replicated record were rebuilt from our own prices, SEC filings, FINRA short interest and option
        volatility and tested one way, with trading costs. Each is checked against the full US record of the same signal
        (212 published predictors on every US stock, failed companies included, to 2025). Then the S&amp;P 500 itself:
        can timing and leverage beat holding it?
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <CircularProgress />}
      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Published signals, every US stock</Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {pa?.median_long_short_pct_month?.in_sample}% → {pa?.median_long_short_pct_month?.after_publication}% → {pa?.median_long_short_pct_month?.recent}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    the median signal's monthly edge in its paper, after publication, and in 2016–2025. Of {pa?.predictors},
                    {' '}<b>{pa?.passes?.length ?? 0}</b> still pass a strict test in 2016–2025 ({(pa?.passes || []).join(', ') || 'none'});
                    among bigger companies (above the NYSE's smallest fifth), <b>{pi?.passes?.length ?? 0}</b>.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">Our test, {lab?.from} → {lab?.to}</Typography>
                  <Typography variant="h5" fontWeight={700}>{lab?.passed?.length ?? 0} of {lab?.signals?.length ?? 0} pass</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Buying a signal's top tenth, after costs, against buying every stock: none clears the bar in both halves.
                    The steadiest is <b>12-month momentum among the 500 most-traded stocks</b>, positive in both halves and in the
                    published record, but not proven. Combining all of them — walk-forward, or with a machine-learning ranker —
                    added nothing.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">The S&amp;P 500, {data.index?.from?.slice(0, 4)} → today</Typography>
                  <Typography variant="h5" fontWeight={700}>{data.index?.adopt?.length ? data.index.adopt.join(', ') : 'No free lunch'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Trend timing keeps most of the return with a smaller worst fall; leverage on top raises the return but
                    brings the fall back — no rule beat holding by a point a year in all three periods without a deeper
                    fall. Keeping cash for dips lost to staying invested.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>This month's momentum book {book?.as_of ? `· ${book.as_of}` : ''}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 950 }}>
              The 500 most-traded stocks ($5+, $3M+ a day), the most volatile fifth dropped, ranked on the return from 12
              months to 1 month ago. It is the sleeve of the evidence portfolio on the Simulation page, refreshed after each
              month's last session and frozen to the Scoreboard. Chosen on the same years it was tested on, so its real test
              is from here on.
            </Typography>
            {!book?.book?.length ? <Alert severity="info">The first book is formed after the close of a month's last session.</Alert> : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 } }}>
                  <TableHead><TableRow>
                    <TableCell>#</TableCell><TableCell>Stock</TableCell><TableCell>Sector</TableCell>
                    <TableCell align="right">12-1 return</TableCell><TableCell align="right">Volatility</TableCell>
                    <TableCell align="right">$ a day</TableCell><TableCell align="right">Days to cover</TableCell>
                    <TableCell align="right">Price</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {book.book.map((b: any) => (
                      <TableRow key={b.symbol}>
                        <TableCell>{b.rank}</TableCell>
                        <TableCell><b>{b.symbol}</b> <Typography component="span" variant="caption" color="text.secondary">{b.name}</Typography></TableCell>
                        <TableCell>{b.sector ?? '–'}</TableCell>
                        <TableCell align="right" sx={{ color: color(b.momentum_pct) }}>{signed(b.momentum_pct, 0)}%</TableCell>
                        <TableCell align="right">{num(b.vol60_pct, 0)}%</TableCell>
                        <TableCell align="right">${num(b.dollar_volume_m, 0)}M</TableCell>
                        <TableCell align="right">{num(b.days_to_cover, 1)}</TableCell>
                        <TableCell align="right">${num(b.price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Portfolio designs, {designs._from} → {designs._to}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 950 }}>
              Monthly, bought at the next day's close, 0.1% a side. The momentum rule was picked after seeing the signal
              results, so these numbers flatter it; the published record (2016–2025, bigger companies: long leg +0.38% a month,
              t 1.5) is its outside support. The best year-by-year steadiness came from dropping the most volatile names —
              the 2020–21 speculative stocks that made the plain sleeve +157% and then −50%.
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 } }}>
                <TableHead><TableRow>
                  <TableCell>Design</TableCell><TableCell align="right">A year</TableCell><TableCell align="right">Sharpe</TableCell>
                  <TableCell align="right">Worst fall</TableCell><TableCell align="right">2017–21</TableCell><TableCell align="right">2022–26</TableCell>
                  <TableCell align="right">vs SPY a month (t)</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {DESIGN_ORDER.filter(k => designs[k]).map(k => {
                    const c = designs[k];
                    return (
                      <TableRow key={k} sx={k === '70% SPY + 30% calmer momentum' ? { bgcolor: 'action.selected' } : undefined}>
                        <TableCell>{k}</TableCell>
                        <TableCell align="right"><b>{num(c.cagr_pct, 1)}%</b></TableCell>
                        <TableCell align="right">{num(c.sharpe)}</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main' }}>{num(c.worst_fall_pct, 1)}%</TableCell>
                        <TableCell align="right">{num(c.halves_cagr_pct?.['2017-2021'], 1)}%</TableCell>
                        <TableCell align="right">{num(c.halves_cagr_pct?.['2022-2026'], 1)}%</TableCell>
                        <TableCell align="right">{k === 'SPY' ? '–' : `${signed(c.vs_spy_pct_month)}% (${num(c.vs_spy_t)})`}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>Every signal</Typography>
              <ToggleButtonGroup size="small" exclusive value={univ} onChange={(_, v) => v && setUniv(v)}>
                <ToggleButton value="large">500 most-traded</ToggleButton>
                <ToggleButton value="small">the rest</ToggleButton>
                <ToggleButton value="all">all</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ my: 1, maxWidth: 950 }}>
              <b>Ours:</b> the signal's top tenth each month minus the average stock, after costs ({lab?.formations} months,
              {' '}{lab?.universe_avg?.[univ] ?? '–'} stocks a month); q is the multiple-testing-adjusted p (below 0.05 is clear).
              {' '}<b>Published:</b> the same signal on every US stock 2016–2025 — top minus bottom tenth, and the top tenth alone
              against the average (what a buyer gets). Positive numbers are the published direction.
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 } }}>
                <TableHead><TableRow>
                  <TableCell>Signal</TableCell><TableCell>Data</TableCell>
                  <TableCell align="right">Ours %/mo (t)</TableCell><TableCell align="right">q</TableCell>
                  <TableCell align="right">2017–21</TableCell><TableCell align="right">2022–26</TableCell>
                  <TableCell align="right">Published top−bottom (t)</TableCell><TableCell align="right">Published top (t)</TableCell>
                  <TableCell>Verdict</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(lab?.signals || []).map((s: any) => {
                    const c = s[univ];
                    const p = s.published_all;
                    return (
                      <TableRow key={s.signal}>
                        <TableCell><Tooltip title={s.signal}><span>{s.what}</span></Tooltip></TableCell>
                        <TableCell>{FAMILY[s.family] ?? s.family}</TableCell>
                        <TableCell align="right" sx={{ color: color(c?.net_pct_month) }}>{c ? `${signed(c.net_pct_month)} (${num(c.t)})` : '–'}</TableCell>
                        <TableCell align="right">{c?.q != null ? num(c.q, 3) : '–'}</TableCell>
                        <TableCell align="right">{signed(c?.halves?.['2017-2021'])}</TableCell>
                        <TableCell align="right">{signed(c?.halves?.['2022-2026'])}</TableCell>
                        <TableCell align="right">{p ? `${signed(p.recent)} (${num(p.recent_t)})` : '–'}</TableCell>
                        <TableCell align="right">{p ? `${signed(p.recent_long_leg)} (${num(p.recent_long_leg_t)})` : '–'}</TableCell>
                        <TableCell><Verdict cell={c} explore={s.explore} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>The S&amp;P 500: timing, leverage and dips since 1950</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 950 }}>
              A year (worst fall) in each period. Leverage is a daily-rebalanced fund: the borrowed part pays T-bills + 0.4%,
              plus 0.9% a year. Growth-trend is the market meter's rule (out only when the trend is down and unemployment is
              rising). The bar to adopt: a point a year better than holding in all three periods with no deeper worst fall.
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 } }}>
                <TableHead><TableRow>
                  <TableCell>Rule</TableCell><TableCell align="right">1950–1987</TableCell><TableCell align="right">1988–now</TableCell>
                  <TableCell align="right">2016–now</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {INDEX_ROWS.filter(k => data.index?.variants?.[k]).map(k => {
                    const v = data.index.variants[k].periods;
                    return (
                      <TableRow key={k}>
                        <TableCell>{k}</TableCell>
                        {['1950-1987', '1988-now', '2016-06-now'].map(per => (
                          <TableCell key={per} align="right">
                            <b>{num(v?.[per]?.cagr_pct, 1)}%</b>{' '}
                            <Typography component="span" variant="caption" color="error.main">({num(v?.[per]?.worst_fall_pct, 0)}%)</Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={600}>Strongest published signals in 2016–2025</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 950 }}>
              Even the best are close to what luck gives when 212 are tried: about five would show t &gt; 2.5 by chance. The
              option-based ones need call and put volatility separately, which the free data does not carry by history.
            </Typography>
            <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
              <TableHead><TableRow>
                <TableCell>Signal</TableCell><TableCell>Paper</TableCell><TableCell align="right">Top−bottom %/mo (t)</TableCell>
                <TableCell align="right">q</TableCell><TableCell align="right">Top alone (t)</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {(pa?.best_recent || []).map((x: any) => (
                  <TableRow key={x.signal}>
                    <TableCell>{x.what}</TableCell><TableCell>{x.year}</TableCell>
                    <TableCell align="right">{signed(x.recent_pct_month)} ({num(x.recent_t)})</TableCell>
                    <TableCell align="right">{num(x.recent_q, 3)}</TableCell>
                    <TableCell align="right">{signed(x.recent_long_leg_pct_month)} ({num(x.recent_long_leg_t)})</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
}
